import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react'
import { useQuery } from '@apollo/client'
import { Stage, Layer } from 'react-konva'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import {
  loadAllSofaElements,
  loadSofaElement,
  type ModuleExports,
} from '@/configurator/DynamicLoadSofaShape'
import { GET_SOFA_TYPES } from '@/components/shared/inputComponents/SofaFormTypeSelect'
import { ChevronDown, Search } from 'lucide-react'
import {
  MAIN_SHAPES,
  getMainShapeTypes,
  type ShapeVariant,
  formatVariantLine,
  variantToProps,
} from '@/configurator/shapeConfig'

interface SofaFormTypeGridSelectProps {
  field?: {
    value: string
    onChange: (value: string) => void
  }
  /**
   * Called with the picked type. When `selectableVariants` is on, picking one
   * of a family's variant thumbnails also passes that variant's params.
   */
  onSelect?: (typeName: string, variant?: ShapeVariant) => void
  /** Make the family variant thumbnails clickable (passes the variant to onSelect). */
  selectableVariants?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

const THUMBNAIL_WIDTH = 120
const THUMBNAIL_HEIGHT = 80
const VARIANT_THUMB_WIDTH = 110
const VARIANT_THUMB_HEIGHT = 100
const ROW_THUMB_WIDTH = 150
const ROW_THUMB_HEIGHT = 100
const BATCH_SIZE = 30

// Fixed reference dimensions — all shapes scale relative to this
// so thumbnails reflect actual proportional sizes
const UNIFORM_REF_WIDTH = 220
const UNIFORM_REF_LENGTH = 220

// Sub-component: renders a small Konva thumbnail of a shape
const ShapeThumbnail = React.memo(
  ({
    shapeModule,
    width: stageWidth,
    height: stageHeight,
    variant,
  }: {
    shapeModule: ModuleExports
    width: number
    height: number
    variant?: ShapeVariant
  }) => {
    const ShapeComponent = shapeModule.default
    const defaults = shapeModule.getDefaultSettings?.()

    const shapeWidth = defaults?.dimensions?.width ?? 100
    const shapeLength = defaults?.dimensions?.length ?? 80

    // Extract remaining dimension defaults (corner_radius, numberOfBigPillows, etc.)
    const DIMENSION_SKIP_KEYS = new Set([
      'width',
      'length',
      'height',
      'armrestPosition',
    ])
    const dimensionProps: Record<string, number | string | boolean> = {}
    if (defaults?.dimensions) {
      for (const [key, value] of Object.entries(defaults.dimensions)) {
        if (DIMENSION_SKIP_KEYS.has(key)) continue
        const camelKey = key.replace(/_([a-z])/g, (_, letter: string) =>
          letter.toUpperCase()
        )
        dimensionProps[camelKey] = value as number | string | boolean
      }
    }

    const padding = 8
    const scaleX = (stageWidth - padding * 2) / UNIFORM_REF_WIDTH
    const scaleY = (stageHeight - padding * 2) / UNIFORM_REF_LENGTH
    const scale = Math.min(scaleX, scaleY)

    const scaledWidth = shapeWidth * scale
    const scaledHeight = shapeLength * scale
    const offsetX = (stageWidth - scaledWidth) / 2
    const offsetY = (stageHeight - scaledHeight) / 2

    const variantProps = variant ? variantToProps(variant) : {}

    return (
      <Stage
        width={stageWidth}
        height={stageHeight}
        listening={false}
        style={{ pointerEvents: 'none' }}
      >
        <Layer x={offsetX} y={offsetY} scaleX={scale} scaleY={scale}>
          <ShapeComponent
            width={shapeWidth}
            height={shapeLength}
            x={0}
            y={0}
            stageWidth={stageWidth}
            stageHeight={stageHeight}
            draggable={false}
            verticalMetric={false}
            horizontalMetric={false}
            showButtons={false}
            layer={null}
            onDelete={null}
            scale={scale}
            {...dimensionProps}
            {...variantProps}
          />
        </Layer>
      </Stage>
    )
  }
)

ShapeThumbnail.displayName = 'ShapeThumbnail'

const ShapePlaceholder = ({
  width,
  height,
}: {
  width: number
  height: number
}) => (
  <div
    className="flex items-center justify-center rounded border border-dashed border-muted-foreground/30"
    style={{ width, height }}
  >
    <span className="text-[10px] text-muted-foreground">No preview</span>
  </div>
)

// Lazy wrapper — only mounts Konva stage when scrolled into view
export const LazyShapeThumbnail = React.memo(
  ({
    shapeModule,
    width,
    height,
    variant,
  }: {
    shapeModule: ModuleExports
    width: number
    height: number
    variant?: ShapeVariant
  }) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const [isVisible, setIsVisible] = useState(false)

    useEffect(() => {
      const el = containerRef.current
      if (!el) return

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible(true)
            observer.disconnect()
          }
        },
        { rootMargin: '200px' }
      )
      observer.observe(el)
      return () => observer.disconnect()
    }, [])

    return (
      <div ref={containerRef} style={{ width, height }}>
        {isVisible ? (
          <ShapeThumbnail
            shapeModule={shapeModule}
            width={width}
            height={height}
            variant={variant}
          />
        ) : (
          <div
            className="animate-pulse rounded bg-muted/50"
            style={{ width, height }}
          />
        )}
      </div>
    )
  }
)

LazyShapeThumbnail.displayName = 'LazyShapeThumbnail'

export const SofaFormTypeGridSelect: React.FC<SofaFormTypeGridSelectProps> = ({
  field,
  onSelect: onSelectProp,
  selectableVariants = false,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}) => {
  const [internalOpen, setInternalOpen] = useState(false)
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen
  const setOpen = controlledOnOpenChange || setInternalOpen
  const [search, setSearch] = useState('')
  const [shapeModules, setShapeModules] = useState<
    Record<string, ModuleExports>
  >({})
  const [modulesLoaded, setModulesLoaded] = useState(false)
  const [searchVisibleCount, setSearchVisibleCount] = useState(BATCH_SIZE)
  const [otherVisibleCount, setOtherVisibleCount] = useState(BATCH_SIZE)
  const [showOther, setShowOther] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const loadMoreObserver = useRef<IntersectionObserver | null>(null)
  const loadMoreOtherObserver = useRef<IntersectionObserver | null>(null)

  const { data, loading, error } = useQuery(GET_SOFA_TYPES)

  // Load all shape modules once when dialog first opens
  useEffect(() => {
    if (open && !modulesLoaded) {
      loadAllSofaElements()
        .then(modules => {
          setShapeModules(modules)
          setModulesLoaded(true)
        })
        .catch(err => {
          console.error('Failed to load sofa shape modules:', err)
          setModulesLoaded(true)
        })
    }
  }, [open, modulesLoaded])

  // Eagerly load the selected module for trigger preview
  useEffect(() => {
    if (field?.value && !shapeModules[field.value] && !modulesLoaded) {
      loadSofaElement(field.value).then(mod => {
        if (mod) setShapeModules(prev => ({ ...prev, [field.value]: mod }))
      })
    }
  }, [field?.value, modulesLoaded, shapeModules])

  // Focus search input when dialog opens, reset on close
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus()
      }, 100)
      return () => clearTimeout(timer)
    } else {
      setSearch('')
      setSearchVisibleCount(BATCH_SIZE)
      setOtherVisibleCount(BATCH_SIZE)
      setShowOther(false)
    }
  }, [open])

  // Reset visible count when search changes
  useEffect(() => {
    setSearchVisibleCount(BATCH_SIZE)
  }, [search])

  // Callback ref for load-more sentinel — sets up observer when element mounts
  const loadMoreSentinelRef = useCallback((node: HTMLDivElement | null) => {
    if (loadMoreObserver.current) {
      loadMoreObserver.current.disconnect()
      loadMoreObserver.current = null
    }
    if (!node) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setSearchVisibleCount(prev => prev + BATCH_SIZE)
        }
      },
      { rootMargin: '200px' }
    )
    observer.observe(node)
    loadMoreObserver.current = observer
  }, [])

  const loadMoreOtherSentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (loadMoreOtherObserver.current) {
        loadMoreOtherObserver.current.disconnect()
        loadMoreOtherObserver.current = null
      }
      if (!node) return

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setOtherVisibleCount(prev => prev + BATCH_SIZE)
          }
        },
        { rootMargin: '200px' }
      )
      observer.observe(node)
      loadMoreOtherObserver.current = observer
    },
    []
  )

  const enumValues: Array<{ name: string; isDeprecated?: boolean }> = useMemo(
    () =>
      [...(data?.__type?.enumValues || [])].sort((a: any, b: any) =>
        a.name?.localeCompare(b.name)
      ),
    [data]
  )

  const filteredValues = useMemo(() => {
    if (!search.trim()) return enumValues
    const term = search.toLowerCase()
    return enumValues.filter(v => v.name.toLowerCase().includes(term))
  }, [enumValues, search])

  const visibleValues = useMemo(
    () => filteredValues.slice(0, searchVisibleCount),
    [filteredValues, searchVisibleCount]
  )
  const hasMore = searchVisibleCount < filteredValues.length

  const mainShapeTypes = useMemo(() => getMainShapeTypes(), [])

  const otherValues = useMemo(
    () => enumValues.filter(v => !mainShapeTypes.has(v.name)),
    [enumValues, mainShapeTypes]
  )

  const visibleOtherValues = useMemo(
    () => otherValues.slice(0, otherVisibleCount),
    [otherValues, otherVisibleCount]
  )
  const hasMoreOther = otherVisibleCount < otherValues.length

  const isSearching = search.trim().length > 0

  const handleSelect = useCallback(
    (typeName: string, variant?: ShapeVariant) => {
      if (onSelectProp) {
        onSelectProp(typeName, variant)
      } else if (field) {
        field.onChange(typeName)
      }
      setOpen(false)
    },
    [onSelectProp, field, setOpen]
  )

  const currentModule = field?.value ? shapeModules[field.value] : null

  if (loading)
    return <div className="text-sm text-muted-foreground">Loading...</div>
  if (error)
    return (
      <div className="text-sm text-destructive">Error loading module types</div>
    )

  // When used in standalone mode (no field), we skip the trigger button entirely
  // The dialog is controlled via open/onOpenChange props
  const isStandalone = !field

  return (
    <>
      {!isStandalone && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={cn(
            'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background',
            'hover:bg-accent hover:text-accent-foreground',
            'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
            'disabled:cursor-not-allowed disabled:opacity-50',
            !field.value && 'text-muted-foreground'
          )}
        >
          <div className="flex items-center gap-2 overflow-hidden">
            {field.value && currentModule && (
              <div className="flex-shrink-0 rounded border border-border bg-muted/30">
                <ShapeThumbnail
                  shapeModule={currentModule}
                  width={40}
                  height={30}
                />
              </div>
            )}
            <span className="truncate">
              {field.value || 'Select a module type'}
            </span>
          </div>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col gap-0 p-0">
          <DialogHeader className="px-6 pt-6 pb-0">
            <DialogTitle>Select Module Type</DialogTitle>
          </DialogHeader>

          {/* Search */}
          <div className="flex items-center border-b px-6 py-2">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              ref={searchInputRef}
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search module types..."
              className="flex h-8 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
            {search && (
              <span className="ml-2 shrink-0 text-xs text-muted-foreground">
                {filteredValues.length} results
              </span>
            )}
          </div>

          {/* Scrollable grid — native overflow scroll */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {isSearching ? (
              /* -- SEARCH MODE: flat filtered results across ALL shapes -- */
              <div className="grid grid-cols-4 gap-3">
                {visibleValues.length === 0 && (
                  <div className="col-span-4 py-12 text-center text-sm text-muted-foreground">
                    No types found.
                  </div>
                )}
                {visibleValues.map(type => {
                  const module = shapeModules[type.name]
                  const isSelected = field?.value === type.name
                  const isDeprecated = type.isDeprecated === true

                  return (
                    <button
                      key={type.name}
                      type="button"
                      onClick={() => handleSelect(type.name)}
                      className={cn(
                        'flex flex-col items-center gap-1 rounded-md border p-2 transition-colors',
                        'hover:bg-accent hover:text-accent-foreground',
                        'focus:outline-none focus:ring-2 focus:ring-ring',
                        isSelected &&
                          'ring-2 ring-primary bg-primary/10 border-primary',
                        isDeprecated && 'opacity-50'
                      )}
                    >
                      <div className="rounded bg-muted/30">
                        {module ? (
                          <LazyShapeThumbnail
                            shapeModule={module}
                            width={THUMBNAIL_WIDTH}
                            height={THUMBNAIL_HEIGHT}
                          />
                        ) : (
                          <ShapePlaceholder
                            width={THUMBNAIL_WIDTH}
                            height={THUMBNAIL_HEIGHT}
                          />
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <span
                          className="max-w-[130px] truncate text-[11px] leading-tight"
                          title={type.name}
                        >
                          {type.name}
                        </span>
                        {isDeprecated && (
                          <span className="rounded bg-muted px-1 py-0.5 text-[9px] text-muted-foreground">
                            OLD
                          </span>
                        )}
                      </div>
                    </button>
                  )
                })}
                {hasMore && (
                  <div
                    ref={loadMoreSentinelRef}
                    className="col-span-4 py-3 text-center text-xs text-muted-foreground"
                  >
                    Loading more... ({searchVisibleCount} /{' '}
                    {filteredValues.length})
                  </div>
                )}
              </div>
            ) : (
              /* -- DEFAULT MODE: Main families + Other toggle -- */
              <>
                {/* Section A: Main Shape Families (horizontal rows) */}
                <div className="flex flex-col divide-y divide-border">
                  {MAIN_SHAPES.map(family => {
                    const module = shapeModules[family.representativeType]
                    const isSelected =
                      field?.value === family.representativeType

                    return (
                      <button
                        key={family.id}
                        type="button"
                        onClick={() => handleSelect(family.representativeType)}
                        className={cn(
                          'flex items-center gap-3 py-5 transition-colors',
                          'hover:bg-accent/50 hover:text-accent-foreground',
                          'focus:outline-none focus:ring-2 focus:ring-ring',
                          isSelected && 'ring-2 ring-primary bg-primary/10'
                        )}
                      >
                        {/* Main shape thumbnail */}
                        <div className="flex-shrink-0 rounded bg-muted/30">
                          {module ? (
                            <LazyShapeThumbnail
                              shapeModule={module}
                              width={ROW_THUMB_WIDTH}
                              height={ROW_THUMB_HEIGHT}
                            />
                          ) : (
                            <ShapePlaceholder
                              width={ROW_THUMB_WIDTH}
                              height={ROW_THUMB_HEIGHT}
                            />
                          )}
                        </div>

                        {/* Label + module type */}
                        <div className="w-[140px] flex-shrink-0 text-left">
                          <span className="block truncate text-[11px] font-medium leading-tight">
                            {family.baseLabel}
                          </span>
                          <span
                            className="block truncate font-mono text-[10px] leading-tight text-muted-foreground"
                            title={family.representativeType}
                          >
                            {family.representativeType}
                          </span>
                        </div>

                        {/* Variant thumbnails */}
                        {family.variants.length > 0 && module && (
                          <div className="flex flex-nowrap gap-1 overflow-x-auto">
                            {family.variants.map((variant, i) => (
                              <div
                                key={i}
                                className={cn(
                                  'rounded bg-muted/20',
                                  selectableVariants &&
                                    'cursor-pointer hover:ring-2 hover:ring-primary/60'
                                )}
                                title={formatVariantLine(variant)}
                                {...(selectableVariants
                                  ? {
                                      role: 'button',
                                      tabIndex: 0,
                                      // Nested inside the family <button>, so
                                      // stop the click from also picking the
                                      // bare family type.
                                      onClick: (e: React.MouseEvent) => {
                                        e.stopPropagation()
                                        handleSelect(
                                          family.representativeType,
                                          variant
                                        )
                                      },
                                      onKeyDown: (e: React.KeyboardEvent) => {
                                        if (e.key !== 'Enter' && e.key !== ' ')
                                          return
                                        e.preventDefault()
                                        e.stopPropagation()
                                        handleSelect(
                                          family.representativeType,
                                          variant
                                        )
                                      },
                                    }
                                  : {})}
                              >
                                <LazyShapeThumbnail
                                  shapeModule={module}
                                  width={VARIANT_THUMB_WIDTH}
                                  height={VARIANT_THUMB_HEIGHT}
                                  variant={variant}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </button>
                    )
                  })}
                </div>

                {/* Section B: Other shapes toggle */}
                <div className="mt-8">
                  <button
                    type="button"
                    onClick={() => setShowOther(prev => !prev)}
                    className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <ChevronDown
                      className={cn(
                        'h-4 w-4 transition-transform',
                        showOther && 'rotate-180'
                      )}
                    />
                    {showOther
                      ? 'Hide other shapes'
                      : `Show all other shapes (${otherValues.length})`}
                  </button>

                  {showOther && (
                    <div className="mt-3 grid grid-cols-4 gap-3">
                      {visibleOtherValues.map(type => {
                        const module = shapeModules[type.name]
                        const isSelected = field?.value === type.name
                        const isDeprecated = type.isDeprecated === true

                        return (
                          <button
                            key={type.name}
                            type="button"
                            onClick={() => handleSelect(type.name)}
                            className={cn(
                              'flex flex-col items-center gap-1 rounded-md border p-2 transition-colors',
                              'hover:bg-accent hover:text-accent-foreground',
                              'focus:outline-none focus:ring-2 focus:ring-ring',
                              isSelected &&
                                'ring-2 ring-primary bg-primary/10 border-primary',
                              isDeprecated && 'opacity-50'
                            )}
                          >
                            <div className="rounded bg-muted/30">
                              {module ? (
                                <LazyShapeThumbnail
                                  shapeModule={module}
                                  width={THUMBNAIL_WIDTH}
                                  height={THUMBNAIL_HEIGHT}
                                />
                              ) : (
                                <ShapePlaceholder
                                  width={THUMBNAIL_WIDTH}
                                  height={THUMBNAIL_HEIGHT}
                                />
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              <span
                                className="max-w-[130px] truncate text-[11px] leading-tight"
                                title={type.name}
                              >
                                {type.name}
                              </span>
                              {isDeprecated && (
                                <span className="rounded bg-muted px-1 py-0.5 text-[9px] text-muted-foreground">
                                  OLD
                                </span>
                              )}
                            </div>
                          </button>
                        )
                      })}
                      {hasMoreOther && (
                        <div
                          ref={loadMoreOtherSentinelRef}
                          className="col-span-4 py-3 text-center text-xs text-muted-foreground"
                        >
                          Loading more... ({otherVisibleCount} /{' '}
                          {otherValues.length})
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default SofaFormTypeGridSelect
